import { randomUUID } from 'node:crypto'
import { ATLAS_WEB_APPLICATION } from '@katoa/schema-provider'
import {
  data,
  href,
  isRouteErrorResponse,
  type ShouldRevalidateFunction,
  useLoaderData,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router'
import { extractErrorMessage } from '../api/extract-error-message'
import { providersClient } from '../api/providers-client'
import { resolveZipLocation } from '../api/zip-location.server'
import { LocationPicker } from '../components/location-picker'
import { ProviderCard } from '../components/provider-card'
import { ResultsHeading } from '../components/results-heading'
import { SearchForm } from '../components/search-form'
import { BackHomeLink, StatusBlock, StatusScreen, TryAgainButton } from '../components/status-screen'
import { logger } from '../instrumentation.server'
import { pageMeta } from '../seo'
import { SITE_NAME } from '../site'
import {
  formatLocationLabel,
  formatLocationSlug,
  formatProviderSlug,
  formatResolvedLocationLabel,
  formatZipSlug,
  parseLocation,
} from '../slug'
import { DEFAULT_SPECIALTY_SLUG, specialtyForSlug } from '../specialties'
import { breadcrumbList, itemList } from '../structured-data'
import { isUsRegionCode } from '../us-regions'
import type { Route } from './+types/results'

/**
 * The providers a results page can actually render, in grid order.
 *
 * A provider with no name can't be rendered as a card or linked to (the detail
 * slug is built from the name), so it isn't a result. Whitespace-only counts as
 * no name: slugify would strip it to '', leaving a blank card behind a
 * "npi-<npi>-" link.
 *
 * Shared by `meta` and the component rather than filtered in each: the heading
 * count, the grid and the `ItemList` markup all have to agree, and the markup
 * claiming more results than the page shows is exactly the kind of mismatch
 * Google treats as a data-quality problem.
 */
const renderable = <T extends { name?: string | null | undefined }>(providers: T[]): (T & { name: string })[] =>
  providers.filter((provider): provider is T & { name: string } => provider.name != null && provider.name.trim() !== '')

export const meta = ({ loaderData, params }: Route.MetaArgs): Route.MetaDescriptors => {
  const specialty = specialtyForSlug(params.specialty ?? '')

  // `loaderData` is undefined when the loader threw (404/500) and the
  // ErrorBoundary renders instead — fall back to the generic wording rather than
  // a broken title and description naming a search that didn't resolve.
  const search = loaderData?.search

  // Names the applied filter so a shared filtered link is self-describing.
  // Absent whenever `?insurance=` didn't resolve — the page is unfiltered then,
  // and the title must not claim otherwise.
  const accepting = loaderData?.insurance != null ? ` accepting ${loaderData.insurance.displayName}` : ''

  // "Cardiologists in Portland, OR" — the page named without its result count or
  // its insurance filter, for the breadcrumb's last rung and the ItemList. The
  // count belongs to the h1 (which changes with the roster) and the filter to the
  // query string (which the canonical drops), so neither belongs in either.
  const searchLabel = search == null ? null : `${search.specialtyPluralLabel} in ${search.locationLabel}`

  // Self-referencing, except where two picker rows share one NUCC code: those two
  // URLs return the same roster, so the duplicate points at the primary and search
  // engines index one of them (VIT-17746 item 5 / the pre-existing behaviour).
  // Carries no query string, so the arbitrary `?insurance=<uuid>` values this route
  // accepts stop competing as distinct indexable URLs.
  const canonicalPath =
    search == null
      ? undefined
      : href('/us/:location/:specialty', {
          location: params.location ?? '',
          specialty: specialty?.canonicalSlug ?? params.specialty ?? '',
        })

  return [
    ...pageMeta({
      title:
        searchLabel == null
          ? 'Your matched doctors | Vital Care Finder'
          : `${searchLabel}${accepting} | Vital Care Finder`,
      description:
        searchLabel == null
          ? 'Care providers matched on cost, quality, and the actual health outcomes of other patients like you.'
          : `${searchLabel}${accepting}, matched on cost, quality, and the actual health outcomes of other patients like you.`,
      canonicalPath,
    }),

    // Only on a page that resolved. Two rungs, not three: the grammar has a
    // `/us/{location}` level (docs/url-structure.md) but no route serves it, and
    // Google requires `item` on every rung except the last — so a "Portland, OR"
    // middle rung would link a 404.
    ...(searchLabel == null
      ? []
      : [
          { 'script:ld+json': breadcrumbList([{ name: SITE_NAME, path: href('/') }, { name: searchLabel }]) },
          // Marks the city × specialty matrix as curated, ordered result sets
          // rather than thin near-duplicate pages. Same path the card links to.
          {
            'script:ld+json': itemList(
              searchLabel,
              renderable(loaderData?.providers?.recommendations ?? []).map((provider) => ({
                name: provider.name,
                path: href('/us/provider/:id', { id: formatProviderSlug(provider.npi, provider.name) }),
              })),
            ),
          },
        ]),
  ]
}

// Forward any loader-set headers onto the outgoing response. Cache-Control is
// set only by the loader's success return, so a thrown 404/500 never picks it
// up and stays uncached at the edge — this route exports its own ErrorBoundary,
// and React Router runs the boundary route's `headers` fn, so a flat headers
// export would stamp s-maxage onto error responses. Mirrors provider.tsx.
export const headers: Route.HeadersFunction = ({ loaderHeaders }) => loaderHeaders

export const shouldRevalidate: ShouldRevalidateFunction = ({ currentParams, nextParams, nextUrl, currentUrl }) => {
  // The search lives in the :location/:specialty path segments, so a new search
  // must re-run the loader; opening/closing a provider detail keeps the same
  // params and skips the reload. The query-string check covers anything else
  // (e.g. mock params) that should refresh results.
  return (
    currentParams['location'] !== nextParams['location'] ||
    currentParams['specialty'] !== nextParams['specialty'] ||
    nextUrl.search !== currentUrl.search
  )
}

// Shared-cache the results page (and its `.data` sibling) at the vit.al edge.
// The honor-origin cache policy in sst.config.ts has defaultTtl 0, so without
// this header every request reaches the SSR Lambda. max-age lets the browser
// reuse the page for an hour without a revalidation round-trip; s-maxage lets
// CloudFront serve it. Unlike home.tsx / support.tsx / terms.tsx / privacy.tsx,
// which keep max-age=0, results are safe to hold in the browser cache.
//
// The invariant this depends on: everything the loader returns is derived only
// from the URL — the path params AND the query string (`?insurance=`) — never
// from request headers or cookies. `reactHonorOriginCachePolicy` keys the full
// query string, and forwards headers without keying them, so a header-derived
// value would serve one viewer's data to everyone. Two consequences:
//
//  * The outside-US branch below tests the :location region code, not the
//    viewer's geo.
//  * The typeahead's `state` relevance hint comes from client-side geo through
//    the `no-store` /resource/insurance-search route, never from this loader.
//
// Set on the loader's non-throwing returns only, so a thrown 404/500 never picks
// it up and stays uncached at the edge (see the `headers` export above).
const CACHE_CONTROL = 'public, max-age=3600, s-maxage=3600, stale-while-revalidate=86400'

/** The resolved `?insurance=` plan — the filter applied, and the label shown. */
type ResolvedInsurance = { id: string; displayName: string; carrierName: string; state: string | null }

// A plan id is a Ribbon uuid. Cheap shape guard so a junk or hostile query
// param costs no upstream round trip.
const PLAN_ID_RE = /^[A-Za-z0-9-]{1,64}$/

/**
 * Resolve `?insurance=<uuid>` to the catalog row it names. Mirrors
 * find-doctor.service.ts's `resolvePlanInsurance`.
 *
 * Every miss degrades identically and non-fatally: no filter applied, no filter
 * named, param ignored. A bad query param must never error a page whose path is
 * valid — and never redirect to strip it either, which would break the back
 * button and add a cache-key hop.
 *
 * A row with no carrier is treated as a miss rather than filtering on the plan
 * alone: `buildInsuranceLadder` gives a plan-only context a single rung with no
 * unfiltered fallback (a cliff), and provider-api only synthesises
 * `insuranceMatches` when a carrier name is set.
 */
const resolveInsurance = async (raw: string | null, nuccCode: string): Promise<ResolvedInsurance | null> => {
  const id = raw?.trim() ?? ''
  if (id === '' || !PLAN_ID_RE.test(id)) return null

  try {
    const response = await providersClient.getRibbonInsurance({ params: { id } })
    if (response.status !== 200) {
      // 404 is the common case — a stale shared link, a hand-typed id, or an id
      // from the live Ribbon passthrough (whose ids this endpoint doesn't know).
      logger.warn({ responseStatus: response.status, nuccCode }, 'results.insurance_plan_unresolved')
      return null
    }

    const carrierName = response.body.carrierName?.trim()
    if (carrierName == null || carrierName === '') {
      logger.info({ nuccCode }, 'results.insurance_plan_has_no_carrier')
      return null
    }

    // `displayName` is non-nullable in the contract but not non-empty, and it is
    // the only string four consumers render. A blank one would apply a filter
    // nothing on the page could name — "accepting " in the title, a heading
    // trailing off — so treat it as unresolvable, exactly like a missing carrier.
    const displayName = response.body.displayName.trim()
    if (displayName === '') {
      logger.warn({ nuccCode, carrierName }, 'results.insurance_plan_has_no_display_name')
      return null
    }

    logger.info({ nuccCode, carrierName }, 'results.insurance_plan_resolved')
    return {
      id: response.body.id,
      displayName,
      carrierName,
      state: response.body.state,
    }
  } catch (error) {
    logger.warn({ error, nuccCode }, 'results.insurance_plan_lookup_failed')
    return null
  }
}

export const loader = async ({ params, request }: Route.LoaderArgs) => {
  const { location, specialty: specialtySlug } = params

  const specialty = specialtyForSlug(specialtySlug)
  const resolvedLocation = parseLocation(location)

  // A specialty and a well-formed location — either <city>--<region> or
  // zip-<5 digits> — are required to search. The route pattern makes both
  // segments mandatory, but either can arrive empty/malformed via a hand-built
  // URL (the form itself always submits both). Surface the error state rather
  // than a blank page.
  if (!specialty || !resolvedLocation) {
    const message = 'Provider search skipped: unresolvable specialty or location path segment'
    // `status` is a Datadog reserved attribute (remapped to log severity), so
    // the HTTP code goes under `responseStatus` to survive ingestion.
    logger.error({ responseStatus: 404 }, message)
    throw data('Not Found', { status: 404 })
  }

  // Resolve a legacy zip- segment to its city/state so the heading and <title>
  // name a place rather than 5 digits (VIT-17833). Started here and awaited
  // alongside the provider search below, so it costs no extra wall-clock. The
  // page stays shared-cacheable: this is still derived purely from the path.
  const zipLocation = resolvedLocation.kind === 'zip' ? resolveZipLocation(resolvedLocation.postalCode) : null

  // Display strings for the heading and <title>. Resolved here rather than
  // re-derived in the component so the path params are parsed exactly once, and
  // before the outside-US branch below so both returns carry them (`meta` reads
  // `loaderData.search` unconditionally).
  const search = {
    specialtyLabel: specialty.label,
    specialtyPluralLabel: specialty.pluralLabel,
    locationLabel: formatResolvedLocationLabel(resolvedLocation),
  }

  // Atlas only has US provider data, so a location we can't cover returns nothing
  // — skip the API call and let the page render the "outside our coverage area"
  // empty state instead of surfacing a geolocation error (VIT-17624). Derived from
  // the path, never from the viewer's geo — see CACHE_CONTROL.
  //
  // Two path forms land here, and neither is a `zip-` segment: those are always in
  // coverage, because the picker only resolves US ZIPs.
  //
  //  * `intl-` — what SearchForm emits for a viewer outside the US. The country is
  //    explicit here, so it can never be confused with a US state (see
  //    INTL_SLUG_RE in slug.ts).
  //  * a `city--region` slug whose region isn't a US state — a hand-built or
  //    pre-`intl-` URL such as "auckland--auk".
  //
  // Testing the discriminant directly (rather than via a precomputed boolean) is
  // what lets the `address` build below narrow `intl` away — every one of them has
  // returned by then.
  if (
    resolvedLocation.kind === 'intl' ||
    (resolvedLocation.kind === 'city' && !isUsRegionCode(resolvedLocation.region))
  ) {
    // `info`, not `error`: a non-US viewer reaching a non-US URL is expected, not
    // a fault — keeping it out of `logger.error` keeps the Datadog error signal
    // clean. Each form logs the fields it actually carries.
    logger.info(
      resolvedLocation.kind === 'intl'
        ? { city: resolvedLocation.city, country: resolvedLocation.country }
        : { city: resolvedLocation.city, region: resolvedLocation.region },
      'Provider search skipped: location outside supported US regions',
    )

    // `providers: null` rather than an empty roster: it pairs with the
    // `outsideUs` discriminant so `useLoaderData` narrows the two returns, and
    // avoids having to name the recommendations element type here.
    //
    // `insurance: null` without resolving: this branch's invariant is that it
    // makes no upstream call at all (VIT-17624). The cost is that the field
    // won't rehydrate on an outside-US URL carrying `?insurance=` — a page that
    // is a dead end anyway.
    return data(
      { outsideUs: true as const, providers: null, search, insurance: null },
      { headers: { 'Cache-Control': CACHE_CONTROL } },
    )
  }

  // Resolved after the outside-US short-circuit above, and sequentially rather
  // than alongside the search — the search body depends on the result, which is
  // the critical-data exemption in docs/front-end/react-router.md. Costs one
  // extra serial hop on filtered pages only, reported for free as
  // `up.providers.getRibbonInsurance` in Server-Timing by the timedClient proxy.
  const insurance = await resolveInsurance(new URL(request.url).searchParams.get('insurance'), specialty.nuccCode)

  // searchProviders accepts a postal code or a city/state pair, never both and
  // never coordinates. A ZIP is the more precise centre of the two (the HUD
  // residential ZIP centroid vs a Census place centroid), which is why the
  // picker persists one.
  const address =
    resolvedLocation.kind === 'zip'
      ? { postalCode: resolvedLocation.postalCode }
      : { city: resolvedLocation.city, state: resolvedLocation.region }

  const [response, zip] = await Promise.all([
    providersClient.searchProviders({
      body: {
        nuccCode: specialty.nuccCode,
        address,
        // Declare the web surface so the search is traced as atlas-web rather than
        // defaulting to the MCP application.
        application: ATLAS_WEB_APPLICATION,
        // atlas-web has no user identity yet; a per-request UUID satisfies the
        // API's required stable-subject field.
        userId: randomUUID(),
        // BOTH, deliberately: buildInsuranceLadder turns plan + carrier into a
        // two-rung ladder that widens plan → carrier when nobody accepts the
        // exact plan. Plan alone is a cliff — the unfiltered rung is appended
        // only when neither is present. Mirrors find-doctor.service.ts.
        // Conditional spread because both fields are `.optional()` and NOT
        // nullable, so an explicit null 400s (exactOptionalPropertyTypes is on).
        ...(insurance != null ? { insurancePlanId: insurance.id, carrierName: insurance.carrierName } : {}),
      },
    }),
    zipLocation,
  ])

  if (response.status !== 200) {
    // 400/500/503 are the contract's declared error statuses; anything else is
    // outside the contract entirely — same enrichment either way, distinct
    // messages so the two cases stay separable in Datadog.
    const message =
      response.status === 400 || response.status === 500 || response.status === 503
        ? 'Handled error response while running provider search'
        : 'Handled unexpected response status while running provider search'
    // `status` and `message` are Datadog reserved attributes (remapped to log
    // severity / the displayed message), so the HTTP code and the upstream API
    // error go under `responseStatus` / `providerMessage` to survive ingestion.
    // The search params make the failing request reproducible from the log.
    logger.error(
      {
        responseStatus: response.status,
        providerMessage: extractErrorMessage(response.status, response.body),
        nuccCode: specialty.nuccCode,
        // Log whichever half of the location the search actually used, under the
        // same field names the two URL forms carry.
        ...(resolvedLocation.kind === 'zip'
          ? { postalCode: resolvedLocation.postalCode }
          : { city: resolvedLocation.city, region: resolvedLocation.region }),
      },
      message,
    )
    // `handleError` (entry.server.tsx) serializes `cause` via pino's err
    // serializer, so the upstream detail also reaches Datadog on the
    // unhandled-error log entry this throw produces.
    throw new Error(message, {
      cause: { responseStatus: response.status, body: response.body },
    })
  }

  return data(
    {
      outsideUs: false as const,
      providers: response.body,
      insurance,
      // formatLocationLabel, not the raw city: the ZIP dataset mirrors HUD, whose
      // PREF_CITY is ALL-CAPS, so "PORTLAND" has to be title-cased (VIT-17805).
      search: {
        ...search,
        ...(zip != null ? { locationLabel: formatLocationLabel({ city: zip.city, region: zip.state }) } : {}),
      },
    },
    { headers: { 'Cache-Control': CACHE_CONTROL } },
  )
}

const Results = () => {
  const loaderData = useLoaderData<typeof loader>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const params = useParams()

  // `providers` is null on the outside-US branch (no API call was made), which
  // flattens to the same empty list as an in-coverage zero-result search — the
  // variant below is what distinguishes the two for the viewer. `renderable` is
  // the same filter `meta` applies, so the heading count, the grid and the
  // ItemList markup can't disagree.
  const providers = renderable(loaderData.providers?.recommendations ?? [])

  const outsideUs = loaderData.outsideUs
  const variant = outsideUs ? 'outside-us' : 'no-providers'
  const insuranceLabel = loaderData.insurance?.displayName ?? null

  return (
    <main data-testid="results-page" className="grow-1 px-3 sm:px-4">
      <SearchForm simple={true} insurance={loaderData.insurance} />

      <div className="max-w-7xl mx-auto space-y-4 py-8">
        <ResultsHeading
          count={providers.length}
          specialtyLabel={loaderData.search.specialtyLabel}
          specialtyPluralLabel={loaderData.search.specialtyPluralLabel}
          insuranceLabel={loaderData.insurance?.displayName ?? null}
          location={
            <LocationPicker
              className="text-primary-400 underline underline-offset-4"
              data-testid="results-location-picker"
              // The search lives in the path, so a new location is a navigation.
              // City/state from the picked ZIP win over the ZIP itself
              // (VIT-17833); a ZIP with no city/state in the dataset keeps the
              // zip- form; a GPS-only pick has no searchable location yet (see
              // location.ts), so it persists and leaves the page where it is.
              //
              // No isUsRegionCode guard on the pick: for a US territory outside
              // US_REGION_CODES (GU, VI) the city-state URL renders the accurate
              // "outside our coverage area" state, which beats a zero-result ZIP
              // page.
              onConfirm={(picked) => {
                const location =
                  picked.city != null && picked.region != null
                    ? formatLocationSlug(picked.city, picked.region)
                    : picked.postalCode != null
                      ? formatZipSlug(picked.postalCode)
                      : null
                if (location === null) return
                navigate(
                  {
                    pathname: href('/us/:location/:specialty', {
                      location,
                      specialty: params['specialty'] ?? DEFAULT_SPECIALTY_SLUG,
                    }),
                    // Carry the query string (e.g. `?insurance=`) so changing
                    // location doesn't silently reset the rest of the search.
                    search: searchParams.toString(),
                  },
                  { viewTransition: true },
                )
              }}
            >
              {loaderData.search.locationLabel}
            </LocationPicker>
          }
        />

        {providers.length === 0 && (
          // A successful search that matched no one — not an error. The heading
          // above already names the (zero) count, specialty and location, so
          // this block adds the illustration and, crucially, what to do next.
          // The SearchForm at the top of the page is the actual way to adjust
          // the search, so this offers guidance rather than a duplicate control.
          //
          // Two reasons to land here, and they need different advice (VIT-17624):
          // a location outside our coverage has nothing to find whatever you
          // change, so pointing at a nearby location or another specialty would
          // be wrong — say coverage is US-only instead. `outsideUs` is on the
          // wrapper as `data-variant` so one selector covers both.
          <div className="py-8 sm:py-12" data-testid="results-empty" data-variant={variant}>
            <StatusBlock
              variant="empty"
              titleAs="h2"
              title="No matches for this search"
              description={
                outsideUs
                  ? 'Sorry, Vital Care Finder is currently only supported within the United States.'
                  : // Only offer to drop the insurance filter when there is one —
                    // otherwise the advice names a control the visitor never used.
                    // Clearing it is the combobox's X plus re-submitting, same as
                    // the ZIP field.
                    `We couldn't find any ${loaderData.search.specialtyPluralLabel.toLowerCase()}${
                      insuranceLabel != null ? ` accepting ${insuranceLabel}` : ''
                    } in ${loaderData.search.locationLabel} right now. Try a nearby location, a different specialty${
                      insuranceLabel != null ? ', or removing your insurance filter.' : '.'
                    }`
              }
            />
          </div>
        )}

        {/* A real list, so a screen reader announces "list, N items" before the
            first card instead of reading a bare run of <article>s (VIT-17746).
            `<li className="flex">` plus `w-full` on the card keeps the equal-height
            grid rows the cards had as direct grid children.

            The explicit `role="list"` is NOT redundant here, which is what the two
            waivers below are about: Tailwind's preflight sets `list-style: none` on
            every `ul` (node_modules/tailwindcss/preflight.css), and Safari+VoiceOver
            drop list semantics from an unstyled list — so without the role this
            announces nothing on the one browser/reader pair most likely to be in
            use. Biome's rules read the attribute against the element in isolation
            and can't see the stylesheet.

            Rendered only when it has contents: an empty `<ul role="list">` is still
            a node in the accessibility tree, so a zero-result or outside-US page
            would announce "list, 0 items" straight after the empty-state block said
            the same thing in prose. The `div` this replaced was silent when empty,
            so the announcement is the role's doing. */}
        {providers.length > 0 && (
          // biome-ignore lint/a11y/useSemanticElements: this already IS a <ul> — the role restores the semantics Tailwind's list-style reset costs it in Safari/VoiceOver
          // biome-ignore lint/a11y/noRedundantRoles: not redundant under `list-style: none`, see above
          <ul role="list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {providers.map((provider) => (
              <li key={provider.npi} className="flex">
                <ProviderCard
                  provider={provider}
                  // Path only, no query string: `/us/*` keys the full query string
                  // at the edge, so forwarding `?insurance=<uuid>` would fragment
                  // the provider page's cache per (provider × plan) — and
                  // `prefetch="viewport"` would fetch each of those `.data`
                  // variants. Nothing is lost: the provider page's back affordance
                  // is `navigate(-1)`, i.e. history, which restores the filtered
                  // results URL intact.
                  detailTo={href('/us/provider/:id', { id: formatProviderSlug(provider.npi, provider.name) })}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  )
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  // Two ways to land here (see the loader): a thrown 404 Response for an
  // unresolvable location/specialty path segment, or a thrown Error when the
  // provider-search API failed (4xx/5xx/network). The 404 is a bad URL the
  // viewer can only escape by starting over; anything else is our problem and
  // may clear on a retry. The upstream detail is deliberately not surfaced —
  // it's already logged for Datadog and means nothing to a visitor.
  const isNotFound = isRouteErrorResponse(error) && error.status === 404

  return isNotFound ? (
    <StatusScreen
      variant="not-found"
      data-testid="results-error"
      title="That search doesn't look right"
      description="We couldn't make sense of that location or specialty. Start a fresh search to find doctors near you."
    >
      <BackHomeLink />
    </StatusScreen>
  ) : (
    <StatusScreen
      variant="error"
      data-testid="results-error"
      title="Something went wrong"
      description="We couldn't load your results just now — it's on us, not you. Please try again in a moment."
    >
      <TryAgainButton />
      <BackHomeLink />
    </StatusScreen>
  )
}

export default Results
